// src/referrals/AffiliatesAdminView.jsx
//
// Admin panel: FirstPromoter affiliate links.
//
// Renders inside CommissionTracker as the "Affiliates" tab (admin/owner only).
// Shows a live list of all FirstPromoter promoters + their copyable referral
// links, and a "Create affiliate link" button to add new ones.
//
// Wiring (in CommissionTracker.jsx):
//   import AffiliatesAdminView from './referrals/AffiliatesAdminView';
//   ...
//   case 'affiliates': return <AffiliatesAdminView currentUser={currentUser} showToast={showToast} />;
//   ...add 'affiliates' to SIDE_NAV with requiresAdmin: true
//
// API: /api/admin/affiliates (GET list, POST create)
// Auth: sends the Supabase access token — same pattern as other admin panels.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';

// ── helpers ────────────────────────────────────────────────────────────────

async function getAccessToken() {
  try {
    const { supabase } = await import('../lib/supabase');
    return readPersistedJwtDirect();
  } catch { return null; }
}

async function apiFetch(path, opts = {}) {
  const token = await getAccessToken();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(json.error || `HTTP ${res.status}`), { json });
  return json;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── sub-components ─────────────────────────────────────────────────────────

function CopyButton({ value, label = 'Copy' }) {
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <button
      onClick={copy}
      className="text-xs px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50 font-medium transition-colors"
    >
      {copied ? '✓ Copied' : label}
    </button>
  );
}

function PromoterRow({ p }) {
  const name = [p.first_name, p.last_name].filter(Boolean).join(' ') || '—';
  const link = p.referral_link || '';

  return (
    <div className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
      {/* Name + email */}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{p.email}</div>
      </div>

      {/* Campaign */}
      {p.campaign_name && (
        <div className="text-xs text-slate-400 hidden md:block shrink-0">
          {p.campaign_name}
        </div>
      )}

      {/* Joined date */}
      <div className="text-xs text-slate-400 shrink-0 hidden sm:block">{fmtDate(p.created_at)}</div>

      {/* Referral link + copy */}
      <div className="flex items-center gap-2 min-w-0">
        {link ? (
          <>
            <span className="text-xs text-slate-600 font-mono truncate max-w-[220px]" title={link}>{link}</span>
            <CopyButton value={link} label="Copy link" />
          </>
        ) : (
          <span className="text-xs text-slate-400 italic">No link yet</span>
        )}
      </div>
    </div>
  );
}

function CreateAffiliateModal({ onClose, onCreated, showToast }) {
  const [firstName, setFirstName] = React.useState('');
  const [lastName,  setLastName]  = React.useState('');
  const [email,     setEmail]     = React.useState('');
  const [loading,   setLoading]   = React.useState(false);
  const [error,     setError]     = React.useState('');
  const [result,    setResult]    = React.useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await apiFetch('/api/admin/affiliates', {
        method: 'POST',
        body: JSON.stringify({ first_name: firstName, last_name: lastName, email }),
      });
      setResult(data.promoter);
      showToast?.(`Affiliate created — link ready for ${email}`, 'success');
      onCreated?.(data.promoter);
    } catch (err) {
      setError(err.message || 'Creation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">Create affiliate link</h3>
          <button aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">✕</button>
        </div>

        {result ? (
          /* Success state — show the generated link */
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="text-emerald-700 font-semibold text-sm mb-1">Affiliate created ✓</div>
              <div className="text-xs text-slate-600">FirstPromoter has emailed <strong>{result.email}</strong> their welcome + referral link.</div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wide font-semibold text-slate-600 block mb-1">Referral link</label>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                <span className="text-xs font-mono text-slate-700 truncate flex-1">{result.referral_link || '(link will appear shortly in FirstPromoter)'}</span>
                {result.referral_link && <CopyButton value={result.referral_link} label="Copy" />}
              </div>
            </div>

            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">Done</button>
            </div>
          </div>
        ) : (
          /* Form state */
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">First name *</span>
                <input
                  autoFocus
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  required
                  placeholder="Jane"
                  className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Last name</span>
                <input
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  placeholder="Smith"
                  className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Email *</span>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="jane@example.com"
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
            </label>

            {error && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3 py-2 text-xs">{error}</div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating…' : 'Create affiliate link'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── main view ──────────────────────────────────────────────────────────────

export default function AffiliatesAdminView({ currentUser, showToast }) {
  const [data,       setData]       = React.useState(null);   // null = loading
  const [error,      setError]      = React.useState('');
  const [search,     setSearch]     = React.useState('');
  const [showCreate, setShowCreate] = React.useState(false);
  const [tick,       setTick]       = React.useState(0);      // refresh trigger

  React.useEffect(() => {
    let cancelled = false;
    setData(null);
    setError('');

    apiFetch('/api/admin/affiliates')
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) { setError(err.message); setData({ promoters: [] }); } });

    return () => { cancelled = true; };
  }, [tick]);

  const promoters = data?.promoters || [];
  const configured = data?.configured !== false;

  const filtered = React.useMemo(() => {
    const lc = search.trim().toLowerCase();
    if (!lc) return promoters;
    return promoters.filter(p =>
      (p.email || '').toLowerCase().includes(lc) ||
      [p.first_name, p.last_name].filter(Boolean).join(' ').toLowerCase().includes(lc)
    );
  }, [promoters, search]);

  const handleCreated = (promoter) => {
    // Optimistically prepend + refresh from API after a short delay
    // (FirstPromoter may take a moment to generate the link).
    setTimeout(() => setTick(t => t + 1), 1500);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-900 to-indigo-700 text-white rounded-2xl p-5">
        <div className="text-xs text-indigo-200 uppercase tracking-wider font-semibold">Admin · FirstPromoter affiliate links</div>
        <h2 className="text-2xl font-bold mt-1">Affiliates</h2>
        <p className="text-sm text-indigo-100 mt-1">
          Create affiliate links for promoters — FirstPromoter emails them their referral link automatically.
        </p>
      </div>

      {/* Not-configured notice */}
      {data && !configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="font-semibold text-amber-800 text-sm mb-1">Connect FirstPromoter</div>
          <p className="text-xs text-amber-700 leading-relaxed">
            Add two env vars in <strong>Vercel → sfa-admin → Settings → Environment Variables</strong> then redeploy:
          </p>
          <pre className="mt-2 bg-white border border-amber-200 rounded-lg p-3 text-xs font-mono text-slate-700 overflow-x-auto">
{data?.message || `FIRSTPROMOTER_API_KEY=your_api_key
FIRSTPROMOTER_ACCOUNT_ID=your_account_id
# optional:
FIRSTPROMOTER_CAMPAIGN_ID=your_campaign_id`}
          </pre>
          <p className="text-xs text-amber-600 mt-2">Get both values from FirstPromoter → Settings → Integrations → Manage API Keys.</p>
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm">{error}</div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold">Promoters</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {data == null ? 'Loading…' : `${filtered.length} of ${promoters.length} affiliates`}
            </p>
          </div>

          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name or email…"
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-52"
          />

          <button
            onClick={() => setTick(t => t + 1)}
            title="Refresh list"
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm hover:bg-white"
          >
            ↻
          </button>

          {configured && (
            <button
              onClick={() => setShowCreate(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
            >
              + Create affiliate link
            </button>
          )}
        </div>

        {/* Table */}
        {data == null ? (
          <div className="p-8 text-center text-slate-400 text-sm">Loading affiliates…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            {promoters.length === 0
              ? (configured ? 'No affiliates yet — create one above.' : 'Configure FirstPromoter to see affiliates here.')
              : 'No matches for that search.'}
          </div>
        ) : (
          <div>
            {/* Column header */}
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 hidden sm:grid grid-cols-[1fr_auto_auto_auto] gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <span>Name / email</span>
              <span className="hidden md:block">Campaign</span>
              <span>Joined</span>
              <span>Referral link</span>
            </div>
            {filtered.map(p => <PromoterRow key={p.id} p={p} />)}
          </div>
        )}
      </div>

      {/* Note about AI-employee usage */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600">
        <div className="font-semibold text-slate-800 mb-1">AI employee / automation usage</div>
        The same <code className="bg-white border border-slate-200 rounded px-1 py-0.5 font-mono">/api/admin/affiliates</code> endpoint
        accepts your <strong>CRON_SECRET</strong> automation token, so workflows and AI agents can create affiliate links programmatically:
        <pre className="mt-2 bg-white border border-slate-200 rounded-lg p-2 font-mono overflow-x-auto">
{`POST /api/admin/affiliates
Authorization: Bearer <CRON_SECRET>
{ "first_name": "Jane", "email": "jane@example.com" }`}
        </pre>
      </div>

      {/* Create modal */}
      {showCreate && (
        <CreateAffiliateModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
          showToast={showToast}
        />
      )}
    </div>
  );
}
